// Hover help for the job page's rollback evaluation profile. Meanings follow
// cd-pipeline-infra's docs/guides/auto-rollback-flags.md and
// cd-deployer/internal/rollback/{profile,plan,catalog}; keep them in step.

export const PROFILE_FIELD_HELP = {
  version:
    'Profiles are versioned. A new version is written when the profile is first bootstrapped, when the profile agent tunes it, or when an operator edits it. Older versions are kept and can be viewed here.',
  current: 'The version the auto-rollback watch uses for the next deploy, why it became current, and when.',
  baseline:
    'What this deployable normally looks like, measured from Datadog in the hour before each of its past deploys. The profile agent tunes the rules from it.',
  runs: 'How many past deploys the baseline was measured from.',
  lowTraffic:
    'Yes when too few requests or invocations were seen to judge the deployable against its own history. A low-traffic profile keeps only the fixed failure checks, no relative rules.',
  built: 'When the baseline was last measured.',
  scope: 'The Datadog scope the baseline and the watch read metrics under.',
  excluded:
    'Past deploys the profile agent left out of the baseline, with the evidence it cited, such as an incident in progress at the time.',
  signals:
    'Per signal, the p50 / p95 / p99 of its value across the pre-deploy windows. Empty when no window had enough traffic to measure.',
  percentile: 'Of the baseline windows, this share had a value at or below this number.',
  rules: 'What the watch checks after each deploy of this job. A rule that holds long enough is a breach.',
  breachesWhen:
    'The condition on the signal’s value. "≥ 0.9" is a fixed bound. "≥ 3× baseline and ≥ +500 over it" is relative: the value must be at least 3 times this deployable’s own value in the hour before the deploy, and at least 500 above it.',
  sustain: 'How many distinct minutes in a row the condition must hold before it counts as a breach.',
  minSample:
    'The fewest requests or invocations a minute needs for its reading to count. Quieter minutes are skipped, neither breaching nor clearing.',
  warmup:
    'Seconds after go-live that this signal’s readings skip, so cold starts don’t count. "—" means none.',
  tier: 'What a sustained breach may do. The environment’s auto-rollback mode can raise or cap it.',
  provenance: 'Who or what wrote this version, and how it was checked.',
  source: 'What wrote this version.',
  created: 'When this version was written.',
  diffFrom: 'The version this one was proposed as a change to. "—" for the first version.',
  model: 'The model the profile agent used to propose this version.',
  promptVersion: 'The version of the profile agent’s prompt that proposed this version.',
  run: 'The watch run file this version was written from, in the rollback-watch-data bucket.',
  validation:
    'The validator’s verdict when this version was written. A version that fails validation is never written.',
} as const;

export const SIGNAL_HELP: Record<string, string> = {
  near_total_failure:
    'The share of invocations (Lambda) or requests (ECS) that failed. A fixed floor every deployable gets: it means "this version is broken".',
  cannot_serve:
    'ECS only: the share of desired tasks actually running, read live from ECS. A fixed floor every ECS service gets.',
  latency: 'p95 latency in milliseconds, compared against this deployable’s own baseline.',
};

export const TIER_HELP: Record<string, string> = {
  record: 'A breach is recorded on the deployment and in the run file. Nothing else happens.',
  detect:
    'A breach is recorded as the watch’s breach and pages rollback_would_fire in prod. In an enforce-mode environment, detect becomes rollback.',
  rca: 'As detect, and the breach is also sent to the rollback root-cause analysis.',
  rollback:
    'A breach may roll the deploy back. Only applies in enforce mode; otherwise it is held at detect.',
};

export const SOURCE_HELP: Record<string, string> = {
  bootstrap:
    'Written automatically after the first watched deploy, from the default rules for this deploy type.',
  agent:
    'Proposed by the profile agent after a watched deploy, and written only because the validator passed it.',
  operator: 'Written by an operator with the deployer CLI.',
};

export const POINTER_REASON_HELP: Record<string, string> = {
  put: 'This version became current when it was written.',
  rollback: 'An operator moved current back to this older version.',
};
